/**
 * Created by Mike Naughton on 10/19/17.
 *
 * A view to handle the presentation logic for importing a layer from a file.
 */

import _ from "underscore";
import Field from "core/models/Field";
import Backbone from "backbone";
import FormView from "core/views/FormView";
import s from "underscore.string";
import URI from "urijs";
import RequestUtils from "app/utils/RequestUtils";
import urlService from "app/UrlService";
import $ from "jquery";

export default FormView.extend({
  /**
   * Initializes the LayerUploadView
   * @param {Object} options An options hash.
   * @param {String} options.url The url for the form.
   * @param options.context The context for the view.
   * @param {String} [options.serviceId] The ID of the service to which this layer should be added.
   */
  initialize: function (options) {
    FormView.prototype.initialize.call(
      this,
      _.defaults({}, options, {
        model: new Backbone.Model({
          id: "",
          name: "",
          latField: "",
          longField: "",
          type: "kmz",
          latFieldEdit: "",
          longFieldEdit: "",
          typeEdit: "kmz",
        }),
        fields: [
          new Field({
            id: "type",
            name: "type",
            editor: "hidden",
          }),
          new Field({
            id: "latField",
            name: "latField",
            editor: "hidden",
          }),
          new Field({
            id: "longField",
            name: "longField",
            editor: "hidden",
          }),
        ],
      })
    );
  },

  _updateUploadFields: function () {
    var latField = this.inputViews["latFieldEdit"];
    var longField = this.inputViews["longFieldEdit"];
    var model = this.options.model;

    if (_.contains(["csv", "xls"], model.get("typeEdit"))) {
      latField.$el.show();
      longField.$el.show();
    } else {
      latField.$("input").val("");
      model.set("latFieldEdit", "");
      latField.$el.hide();

      longField.$("input").val("");
      model.set("longFieldEdit", "");
      longField.$el.hide();
    }
  },

  _updateUploadType: function () {
    var fileField = this.inputViews["file"];
    var model = fileField.model;
    var fileName = model.get("file");

    if (s.endsWith(fileName, ".kmz")) {
      model.set("typeEdit", "kmz");
    } else if (s.endsWith(fileName, ".kml")) {
      model.set("typeEdit", "kml");
    } else if (s.endsWith(fileName, ".zip")) {
      model.set("typeEdit", "shp");
    } else if (s.endsWith(fileName, ".json")) {
      model.set("typeEdit", "json");
    } else if (s.endsWith(fileName, ".csv")) {
      model.set("typeEdit", "csv");
    } else if (s.endsWith(fileName, ".xls") || s.endsWith(fileName, ".xlsx")) {
      model.set("typeEdit", "xls");
    } else if (s.endsWith(fileName, ".dxf")) {
      model.set("typeEdit", "dxf");
    }
  },

  _getVisibleFields: function () {
    return [
      new Field({
        id: "name",
        name: "name",
        label: "Name",
        editor: "text",
      }),
      new Field({
        id: "file",
        name: "file",
        label: "File",
        editor: "custom",
        EditorView: {
          template: _.template(
            '<input class="form-control" type="<%-type%>" name="<%-name%>" value="<%-value%>"/>'
          ),

          events: {
            "change .form-control": "onChange",
            "keyup .form-control": "onChange",
          },

          getControlValue: function () {
            return this.$(".form-control").val();
          },

          setControlValue: function (value) {
            this.$(".form-control").val(value);
          },

          serialize: function () {
            return {
              type: "file",
              name: this.field.id,
              value: this.getValue(""),
            };
          },
        },
      }),
      new Field({
        id: "typeEdit",
        name: "typeEdit",
        label: "Import Type",
        values: [
          {
            value: "kmz",
            text: "Google KMZ",
          },
          {
            value: "kml",
            text: "Google KML",
          },
          {
            value: "shp",
            text: "ESRI Shapefile",
          },
          {
            value: "json",
            text: "JSON",
          },
          {
            value: "csv",
            text: "CSV",
          },
          {
            value: "xls",
            text: "Excel Spreadsheet",
          },
          {
            value: "dxf",
            text: "AutoCAD DXF",
          },
        ],
      }),
      new Field({
        id: "latFieldEdit",
        name: "latFieldEdit",
        label: "Latitude Field Name",
        editor: "text",
      }),
      new Field({
        id: "longFieldEdit",
        name: "longFieldEdit",
        label: "Longitude Field Name",
        editor: "text",
      }),
    ];
  },

  render: function () {
    FormView.prototype.render.apply(this, arguments);
    var model = this.options.model;

    _.each(this.inputViews, function (inputView) {
      inputView.$el.hide();
    });
    _.each(
      this._getVisibleFields(),
      function (visibleField) {
        this.addField(visibleField);
      },
      this
    );

    function registerEditChange(fieldName) {
      model.on("change:" + fieldName + "Edit", function () {
        model.set(fieldName, model.get(fieldName + "Edit"));
      });
    }

    if (this.options.serviceId) {
      this.$el.prepend(
        '<input type="hidden" name="service" value="' +
          this.options.serviceId +
          '"/>'
      );
      model.set("serviceId", this.options.serviceId);
    }

    model.on("change:type", this._updateUploadFields, this);
    model.on("change:file", this._updateUploadType, this);
    registerEditChange("latField");
    registerEditChange("longField");
    registerEditChange("type");
    this._updateUploadFields();
  },

  /**
   * Builds and sets an upload URL based on the selections made on the form.
   */
  _updateUploadUrl: function () {
    var uploadUri = URI(this.$el.attr("action"));

    uploadUri.addSearch("latField", this.options.model.get("latField"));
    uploadUri.addSearch("longField", this.options.model.get("longField"));
    uploadUri.addSearch("type", this.options.model.get("type"));
    this.$el.attr("action", uploadUri.toString());
  },

  save: function () {
    var defer = new $.Deferred();
    var apiUrl = urlService.apiurl("/layers/newUploadUrl");
    var type = this.model.get("type");
    var self = this;

    var reject = function (err) {
      defer.reject(err);
    };

    this._updateUploadUrl();
    RequestUtils.post(
      apiUrl,
      { type: type },
      function (response) {
        var putUrl = response.putUrl;
        var fileKey = response.fileKey;
        var contentType = response.mimeType;

        //todo better way to get the file
        var fileField = self.inputViews["file"];
        var file = fileField.$(".form-control")[0].files[0];

        $.ajax(putUrl, {
          data: file,
          processData: false,
          type: "PUT",
          contentType: contentType,
          cache: false,
        })
          .done(function () {
            //Remove file, add file key. Post with with formView
            var fileModel = fileField.model;
            fileModel.set("file", "");
            self.$el.prepend(
              '<input type="hidden" name="fileKey" value="' + fileKey + '"/>'
            );

            FormView.prototype.save
              .apply(self, arguments)
              .done(function (response) {
                defer.resolve(response);
              })
              .fail(reject);
          })
          .fail(reject);
      },
      reject
    );

    return defer.promise();
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/layer/LayerUploadView.js
// module id = 492
// module chunks = 1